import React, { useState } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Paper,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    ArrowBackIos,
    ArrowForwardIos,
    Close,
} from '@mui/icons-material';

export interface CarouselImage {
    id: number;
    imageUrl: string;
    imagedesc?: string;
    like?: number;
    share?: number;
}

interface ImageCarouselProps {
    images: CarouselImage[];
    initialIndex?: number;
    onClose?: () => void;
    albumTitle?: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
    images,
    initialIndex = 0,
    onClose,
    albumTitle,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!images || images.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6">No images available</Typography>
            </Box>
        );
    }

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowLeft') handlePrevious();
        if (e.key === 'ArrowRight') handleNext();
        if (e.key === 'Escape' && onClose) onClose();
    };

    const currentImage = images[currentIndex];

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            onKeyDown={handleKeyDown}
            tabIndex={0}
        >
            {/* Header with title and close button */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    zIndex: 10000,
                }}
            >
                {albumTitle && (
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'white',
                            fontWeight: 600,
                            ml: 2,
                        }}
                    >
                        {albumTitle}
                    </Typography>
                )}
                {onClose && (
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        }}
                    >
                        <Close />
                    </IconButton>
                )}
            </Box>

            {/* Image counter */}
            <Box
                sx={{
                    position: 'absolute',
                    top: { xs: '60px', sm: '80px' },
                    right: 16,
                    zIndex: 10000,
                }}
            >
                <Paper
                    sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                    }}
                >
                    <Typography variant="body2">
                        {currentIndex + 1} / {images.length}
                    </Typography>
                </Paper>
            </Box>

            {/* Main image container */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 1, sm: 4 },
                    py: { xs: 8, sm: 4 },
                }}
            >
                {/* Previous button */}
                {images.length > 1 && (
                    <IconButton
                        onClick={handlePrevious}
                        sx={{
                            position: 'absolute',
                            left: { xs: 8, sm: 16 },
                            color: 'white',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                            zIndex: 10001,
                        }}
                        size={isMobile ? 'small' : 'large'}
                    >
                        <ArrowBackIos />
                    </IconButton>
                )}

                {/* Image */}
                <Box
                    sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <img
                        src={currentImage.imageUrl}
                        alt={currentImage.imagedesc || `Image ${currentIndex + 1}`}
                        style={{
                            maxWidth: '100%',
                            maxHeight: isMobile ? '70vh' : '85vh',
                            objectFit: 'contain',
                            borderRadius: '8px',
                        }}
                    />
                    {currentImage.imagedesc && (
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'white',
                                mt: 2,
                                textAlign: 'center',
                                maxWidth: '80%',
                                px: 2,
                            }}
                        >
                            {currentImage.imagedesc}
                        </Typography>
                    )}
                </Box>

                {/* Next button */}
                {images.length > 1 && (
                    <IconButton
                        onClick={handleNext}
                        sx={{
                            position: 'absolute',
                            right: { xs: 8, sm: 16 },
                            color: 'white',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                            zIndex: 10001,
                        }}
                        size={isMobile ? 'small' : 'large'}
                    >
                        <ArrowForwardIos />
                    </IconButton>
                )}
            </Box>

            {/* Thumbnail navigation (desktop only) */}
            {!isMobile && images.length > 1 && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                        p: 2,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        overflowX: 'auto',
                        maxWidth: '100%',
                    }}
                >
                    {images.map((image, index) => (
                        <Box
                            key={image.id}
                            onClick={() => setCurrentIndex(index)}
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: 1,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: currentIndex === index ? '3px solid white' : '3px solid transparent',
                                opacity: currentIndex === index ? 1 : 0.6,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    opacity: 1,
                                    transform: 'scale(1.05)',
                                },
                                flexShrink: 0,
                            }}
                        >
                            <img
                                src={image.imageUrl}
                                alt={`Thumbnail ${index + 1}`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default ImageCarousel;

